<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="800">
    <v-card>
      <v-card-title class="headline">
        <v-icon left color="primary">mdi-star-outline</v-icon>
        Choose Your Plan
      </v-card-title>

      <v-card-text>
        <v-alert type="info" dense class="mb-4">
          Select the plan that fits your video creation needs. All plans include our core features with different usage limits.
        </v-alert>

        <v-alert type="warning" dense class="mb-4" v-if="showFreeLimitsWarning">
          <strong>Free Plan Limitations:</strong> With the free plan, you can only play up to 3 segments in your videos and must be logged in to make any edits. Upgrade to Pro for unlimited access and advanced features.
        </v-alert>

        <div class="pricing-cards">
          <!-- Free Plan -->
          <div class="pricing-card" :class="{ 'selected-plan': localSelectedPlan === 'free' }" @click="selectPlan('free')">
            <div class="pricing-header">
              <div class="plan-name">
                <v-icon small>mdi-rocket-launch-outline</v-icon>
                <span>Free</span>
              </div>
              <div class="plan-price">
                <span class="price">$0</span>
                <span class="period">/month</span>
              </div>
              <div class="plan-description">Get started with video creation</div>
            </div>

            <div class="plan-features">
              <div class="feature-item">
                <v-icon small color="success">mdi-check</v-icon>
                <span>Basic AI video generation</span>
              </div>
              <div class="feature-item">
                <v-icon small color="success">mdi-check</v-icon>
                <span>5 file & text submissions</span>
              </div>
              <div class="feature-item">
                <v-icon small color="success">mdi-check</v-icon>
                <span>3 AI-powered video queries</span>
              </div>
              <div class="feature-item">
                <v-icon small color="error">mdi-alert-circle</v-icon>
                <span class="free-limitation">Limited to 3 video segments</span>
              </div>
              <div class="feature-item">
                <v-icon small color="success">mdi-check</v-icon>
                <span>15 search operations</span>
              </div>
              <div class="feature-item">
                <v-icon small color="success">mdi-check</v-icon>
                <span>5 projects & galaxies</span>
              </div>
              <div class="feature-item">
                <v-icon small color="warning">mdi-alert</v-icon>
                <span class="free-limitation">Login required for editing</span>
              </div>
            </div>

            <div class="plan-action">
              <v-btn
                outlined
                block
                :color="localSelectedPlan === 'free' ? 'primary' : ''"
                @click.stop="selectPlan('free')"
              >
                {{ localSelectedPlan === 'free' ? 'Current Plan' : 'Select Plan' }}
              </v-btn>
            </div>
          </div>

          <!-- Pro Plan -->
          <div class="pricing-card" :class="{ 'selected-plan': localSelectedPlan === 'pro' }" @click="selectPlan('pro')">
            <div class="pricing-header">
              <div class="plan-badge">POPULAR</div>
              <div class="plan-name">
                <v-icon small>mdi-star-three-points-outline</v-icon>
                <span>Pro</span>
              </div>
              <div class="plan-price">
                <span class="price">$149</span>
                <span class="period">/month</span>
              </div>
              <div class="plan-description">For professionals & creators</div>
            </div>

            <div class="plan-features">
              <div class="feature-item">
                <v-icon small color="success">mdi-check</v-icon>
                <span>Everything in Free, plus:</span>
              </div>
              <div class="feature-item">
                <v-icon small color="success">mdi-check</v-icon>
                <span>3,000 AI-powered video queries</span>
              </div>
              <div class="feature-item highlight-feature">
                <v-icon small color="success">mdi-check-circle</v-icon>
                <span>Unlimited video segments</span>
              </div>
              <div class="feature-item">
                <v-icon small color="success">mdi-check</v-icon>
                <span>500 file & text submissions</span>
              </div>
              <div class="feature-item">
                <v-icon small color="success">mdi-check</v-icon>
                <span>Premium LLM access (1,500 queries)</span>
              </div>
              <div class="feature-item">
                <v-icon small color="success">mdi-check</v-icon>
                <span>Advanced video customization</span>
              </div>
              <div class="feature-item">
                <v-icon small color="success">mdi-check</v-icon>
                <span>Unlimited projects & galaxies</span>
              </div>
              <div class="feature-item">
                <v-icon small color="success">mdi-check</v-icon>
                <span>Priority customer support</span>
              </div>
            </div>

            <div class="plan-action">
              <v-btn
                color="primary"
                block
                @click.stop="selectPlan('pro')"
              >
                {{ localSelectedPlan === 'pro' ? 'Current Plan' : 'Upgrade Now' }}
              </v-btn>
            </div>
          </div>

          <!-- Pro Team Plan -->
          <div class="pricing-card" :class="{ 'selected-plan': localSelectedPlan === 'team_pro' }" @click="selectPlan('team_pro')">
            <div class="pricing-header">
              <div class="plan-name">
                <v-icon small>mdi-account-group</v-icon>
                <span>Pro Team</span>
              </div>
              <div class="plan-price">
                <span class="price">$399</span>
                <span class="period">/month</span>
              </div>
              <div class="plan-description">For teams of 5 members</div>
            </div>

            <div class="plan-features">
              <div class="feature-item">
                <v-icon small color="success">mdi-check</v-icon>
                <span>Everything in Pro, plus:</span>
              </div>
              <div class="feature-item">
                <v-icon small color="success">mdi-check</v-icon>
                <span>Collaboration for 5 team members</span>
              </div>
              <div class="feature-item">
                <v-icon small color="success">mdi-check</v-icon>
                <span>6,000 AI-powered video queries</span>
              </div>
              <div class="feature-item">
                <v-icon small color="success">mdi-check</v-icon>
                <span>Premium LLM access (3,000 queries)</span>
              </div>
              <div class="feature-item">
                <v-icon small color="success">mdi-check</v-icon>
                <span>Advanced team analytics</span>
              </div>
              <div class="feature-item">
                <v-icon small color="success">mdi-check</v-icon>
                <span>Shared workspaces & resources</span>
              </div>
              <div class="feature-item">
                <v-icon small color="success">mdi-check</v-icon>
                <span>Dedicated account manager</span>
              </div>
            </div>

            <div class="plan-action">
              <v-btn
                outlined
                block
                :color="localSelectedPlan === 'team_pro' ? 'primary' : ''"
                @click.stop="selectPlan('team_pro')"
              >
                {{ localSelectedPlan === 'team_pro' ? 'Current Plan' : 'Upgrade Team' }}
              </v-btn>
            </div>
          </div>
        </div>

        <v-divider class="my-4"></v-divider>

        <div class="text-body-2 mt-4">
          <div class="features-comparison">
            <h3 class="subtitle-1 mb-3">Plan Comparison</h3>

            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Feature</th>
                    <th class="text-center">Free</th>
                    <th class="text-center">Pro</th>
                    <th class="text-center">Pro Team</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(feature, index) in comparisonFeatures" :key="index">
                    <td>{{ feature.name }}</td>
                    <td class="text-center">{{ feature.free }}</td>
                    <td class="text-center">{{ feature.pro }}</td>
                    <td class="text-center">{{ feature.team }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>

          <div class="mt-4 payment-info">
            <p>
              <v-icon small left>mdi-credit-card</v-icon>
              <strong>Secure Payment:</strong> All payments processed via Stripe with industry-standard security.
            </p>
            <p>
              <v-icon small left>mdi-cancel</v-icon>
              <strong>Cancel Anytime:</strong> No long-term commitment required. Cancel your subscription at any time.
            </p>
            <p v-if="localSelectedPlan === 'team_pro'">
              <v-icon small left>mdi-account-multiple-plus</v-icon>
              <strong>Need More Seats?</strong> Additional team members available at $59/member/month.
            </p>
          </div>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          text
          @click="$emit('input', false)"
          :disabled="loading"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          @click="confirmSelectedPlan"
          :loading="loading"
          :disabled="!localSelectedPlan || localSelectedPlan === 'free'"
        >
          <span class="black--text">Continue</span>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'UpgradeDialog',

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    selectedPlan: {
      type: String,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      localSelectedPlan: this.selectedPlan || 'pro',
      showFreeLimitsWarning: true,
      comparisonFeatures: [
        {
          name: 'Video Queries',
          free: '3',
          pro: '3,000',
          team: '6,000',
        },
        {
          name: 'Video Segments',
          free: '3 only',
          pro: 'Unlimited',
          team: 'Unlimited',
        },
        {
          name: 'Premium LLM Queries',
          free: '3',
          pro: '1,500',
          team: '3,000',
        },
        {
          name: 'File Submissions',
          free: '5',
          pro: '500',
          team: '1,000',
        },
        {
          name: 'Text Submissions',
          free: '5',
          pro: '500',
          team: '1,000',
        },
        {
          name: 'YouTube Transcript Submissions',
          free: '5',
          pro: '1,000',
          team: '2,000',
        },
        {
          name: 'Projects',
          free: '5',
          pro: '500',
          team: '1,000',
        },
        {
          name: 'Galaxies',
          free: '5',
          pro: '1,000',
          team: '2,000',
        },
        {
          name: 'Universes',
          free: '1',
          pro: '1',
          team: '5',
        },
        {
          name: 'Team Collaboration',
          free: '❌',
          pro: '❌',
          team: '✅',
        },
        {
          name: 'Priority Support',
          free: '❌',
          pro: '✅',
          team: '✅',
        }
      ]
    };
  },

  watch: {
    selectedPlan(newValue) {
      if (newValue) {
        this.localSelectedPlan = newValue;
      }
    },
    value(newValue) {
      // Reset to default selection if dialog is reopened
      if (newValue && this.selectedPlan) {
        this.localSelectedPlan = this.selectedPlan;
      } else if (newValue && !this.selectedPlan) {
        // Default to pro if no plan is selected
        this.localSelectedPlan = 'pro';
      }
    }
  },

  methods: {
    selectPlan(plan) {
      this.localSelectedPlan = plan;
      this.$emit('update:selectedPlan', plan);
    },

    confirmSelectedPlan() {
      if (this.localSelectedPlan && this.localSelectedPlan !== 'free') {
        this.$emit('confirm', this.localSelectedPlan);
      }
    }
  }
};
</script>

<style scoped>
.pricing-cards {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.pricing-card {
  flex: 1;
  min-width: 200px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  cursor: pointer;
}

.pricing-card:hover {
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.selected-plan {
  border-color: #1976d2;
  box-shadow: 0 6px 12px rgba(25, 118, 210, 0.15);
}

.plan-badge {
  position: absolute;
  top: 10px;
  right: -28px;
  background-color: #1976d2;
  color: white;
  font-size: 10px;
  font-weight: bold;
  padding: 4px 30px;
  transform: rotate(45deg);
}

.pricing-header {
  margin-bottom: 20px;
  text-align: center;
}

.plan-name {
  font-weight: bold;
  font-size: 1.2rem;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.free-limitation {
  color: #ff5252;
}

.highlight-feature {
  color: #4caf50;
  font-weight: 500;
}

.plan-limitations {
  color: #ff5252;
  font-style: italic;
}

.plan-price {
  margin-bottom: 10px;
}

.plan-price .price {
  font-size: 2rem;
  font-weight: bold;
  color: #1976d2;
}

.plan-price .period {
  font-size: 0.9rem;
}

.plan-description {
  font-size: 0.9rem;
  margin-bottom: 5px;
}

.plan-features {
  margin-bottom: 20px;
  flex-grow: 1;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  font-size: 0.9rem;
}

.feature-item .v-icon {
  margin-right: 8px;
  margin-top: 2px;
}

.plan-action {
  margin-top: auto;
}

.payment-info p {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.payment-info .v-icon {
  margin-right: 8px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .pricing-cards {
    flex-direction: column;
  }

  .pricing-card {
    width: 100%;
    margin-bottom: 16px;
  }
}
</style>
